import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { DPSLayout } from '../../components/dps/layout/DPSLayout';
import { ActionProjectionBanner } from '../../components/dps/actions/ActionProjectionBanner';
import { ActionCompletionModal } from '../../components/dps/actions/ActionCompletionModal';
import { dpsTheme, getPriorityLabel, getStatusLabel } from '../../styles/dpsTheme';
import { pdf } from '@react-pdf/renderer';
import PDFDocument from '../../components/results/PDFDocument';
import toast from 'react-hot-toast';
import { ensureActionsExist } from '../../services/repairService';
import { Info, DollarSign, Star, Check, FileCheck } from 'lucide-react';
import { EquipmentDetailsModal } from '../../components/dps/actions/EquipmentDetailsModal';
import { calculateSecurityScore } from '../../utils/securityCalculator';
import { useDeviceSize } from '../../hooks/useMediaQuery';

// Mapping entre les IDs d'actions et les IDs d'équipements
const ACTION_TO_EQUIPMENT_MAP: Record<string, string> = {
  'install-reinforced-door': 'door',
  'install-alarm-system': 'alarm',
  'install-cameras': 'camera',
  'install-smart-lock': 'smartLock',
  'install-outdoor-lighting': 'lighting',
  'install-video-doorbell': 'videoDoorbell',
  'install-window-locks': 'windowLocks',
  'install-motion-sensors': 'motionSensor',
  'install-door-sensors': 'doorSensor',
  'install-safe': 'safe',
  'install-window-bars': 'bars',
  'install-intercom': 'intercom',
  'install-fog-system': 'fogSystem',
  'secure-perimeter': 'securityGate',
  'upgrade-door-locks': 'multipointLock',
  'upgrade-alarm-monitoring': 'remoteSurveillance',
  'install-window-film': 'windowFilm',
  'install-door-bar': 'doorBar',
  'install-security-film': 'windowFilm', 
  'install-presence-simulator': 'lightTimer', 
  'install-timer-switches': 'lightTimer', 
  'install-security-signs': 'securityStickers', 
  'install-fake-cameras': 'dummyCamera', 
};

// Fonction de tri par priorité
const sortActionsByPriority = (actions: any[]) => {
  const priorityOrder: { [key: string]: number } = {
    'high': 1,
    'medium': 2,
    'low': 3
  };

  return [...actions].sort((a, b) => {
    const priorityA = priorityOrder[a.priority] || 999;
    const priorityB = priorityOrder[b.priority] || 999;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return (b.score_impact || 0) - (a.score_impact || 0);
  });
};

type Priority = 'high' | 'medium' | 'low';
type Status = 'completed' | 'planned' | 'in_progress' | 'cancelled';

export const DPSActions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { userProfile } = useAuth();
  const { isMobile } = useDeviceSize();
  const [diagnostic, setDiagnostic] = useState<any>(null);
  const [actions, setActions] = useState<any[]>([]);
  const [filteredActions, setFilteredActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'all' | Priority>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<any>(null);
  const [isRepairing, setIsRepairing] = useState(false);
  const [simulatedActions, setSimulatedActions] = useState<Set<string>>(new Set());
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredActions(actions);
    } else {
      setFilteredActions(actions.filter(a => a.priority === activeFilter));
    }
  }, [activeFilter, actions]);

  const loadData = async () => {
    if (!id) return;

    try {
      const { data: diagData, error: diagError } = await supabase
        .from('diagnostics')
        .select('*')
        .eq('id', id)
        .single();

      if (diagError) throw diagError;
      setDiagnostic(diagData);

      const { data: actionsData, error: actionsError } = await supabase
        .from('improvement_actions_history')
        .select('*')
        .eq('diagnostic_id', id);

      if (actionsError) throw actionsError;

      if (!actionsData || actionsData.length === 0) {
        setIsRepairing(true);
        const repaired = await ensureActionsExist(id);
        setIsRepairing(false);

        if (repaired) {
          const { data: repairedActions } = await supabase
            .from('improvement_actions_history')
            .select('*')
            .eq('diagnostic_id', id);

          const sortedActions = sortActionsByPriority(repairedActions || []);
          setActions(sortedActions);
          setFilteredActions(sortedActions);
          toast.success('Plan d\'action généré automatiquement');
        } else {
          setActions([]);
          setFilteredActions([]);
        }
      } else {
        const sortedActions = sortActionsByPriority(actionsData);
        setActions(sortedActions);
        setFilteredActions(sortedActions);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!diagnostic) return;

    try {
      const diagnosticData = diagnostic.diagnostic_data;
      if (!diagnosticData) {
        toast.error('Données de diagnostic manquantes');
        return;
      }

      const blob = await pdf(
        <PDFDocument
          formData={diagnosticData}
          improvementActions={actions}
          companyName={userProfile?.company_name}
          companyLogoUrl={userProfile?.company_logo_url ?? undefined}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `DPS-${diagnosticData.city || 'rapport'}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const toggleSimulation = (actionId: string) => {
    setSimulatedActions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  const calculateProjectedScore = () => {
    const selectedActions = actions.filter(a => simulatedActions.has(a.id));
    const totalImpact = selectedActions.reduce((sum, a) => sum + Number(a.score_impact), 0);
    const totalCost = selectedActions.reduce((sum, a) => sum + Number(a.estimated_cost), 0);
    return {
      projectedScore: Math.min(100, (diagnostic.security_score || diagnostic.score || 0) + totalImpact),
      estimatedCost: totalCost,
      selectedCount: selectedActions.length
    };
  };

  const handlePlanAction = async (actionId: string) => {
    try {
      const { error } = await supabase
        .from('improvement_actions_history')
        .update({ status: 'planned', updated_at: new Date().toISOString() })
        .eq('id', actionId);

      if (error) throw error;
      toast.success('Action planifiée');
      
      setSimulatedActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(actionId);
        return newSet;
      });
      
      loadData();
    } catch (error) {
      console.error('Error planning action:', error);
      toast.error('Erreur lors de la planification');
    }
  };

  const handleStartAction = async (actionId: string) => {
    try {
      const { error } = await supabase
        .from('improvement_actions_history')
        .update({ status: 'in_progress', updated_at: new Date().toISOString() })
        .eq('id', actionId);

      if (error) throw error;
      toast.success('Action démarrée');
      loadData();
    } catch (error) {
      console.error('Error starting action:', error);
      toast.error('Erreur lors du démarrage');
    }
  };

  const handleCancelAction = async (actionId: string) => {
    try {
      const { error } = await supabase
        .from('improvement_actions_history')
        .update({
          status: 'not_planned',
          planned_date: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', actionId);

      if (error) throw error;
      toast.success('Action annulée');
      loadData();
    } catch (error: any) {
      console.error('Error canceling action:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const handleCompleteAction = (action: any) => {
    setSelectedAction(action);
    setModalOpen(true);
  };

  const handleConfirmCompletion = async (actualCost: number, notes: string) => {
  if (!selectedAction) return;

  try {
    // 1. Marquer l'action comme complétée
    const { error: actionError } = await supabase
      .from('improvement_actions_history')
      .update({
        status: 'completed',
        actual_cost: actualCost,
        notes: notes,
        implemented_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', selectedAction.id);

    if (actionError) throw actionError;

    // 2. Récupérer le diagnostic complet
    const { data: diagData, error: diagError } = await supabase
      .from('diagnostics')
      .select('*')
      .eq('id', id)
      .single();

    if (diagError) throw diagError;

    // 3. Extraire les données du formulaire
    const formData = diagData.diagnostic_data || {};

    // 4. Ajouter l'équipement correspondant à l'action dans la liste des équipements
    const equipmentId = ACTION_TO_EQUIPMENT_MAP[selectedAction.action_id];
    
    if (equipmentId && formData.equipment) {
      if (!formData.equipment.includes(equipmentId)) {
        formData.equipment = [...formData.equipment, equipmentId];
      }
    }

    // 5. Recalculer le score complet
    const { score: newScore, categoryScores } = calculateSecurityScore(formData);

    // 6. Mettre à jour le diagnostic avec le nouveau score
    const { error: updateError } = await supabase
      .from('diagnostics')
      .update({ 
        security_score: newScore,
        category_scores: {
          physicalProtection: categoryScores.protection,
          surveillance: categoryScores.surveillance,
          deterrence: categoryScores.deterrence
        },
        diagnostic_data: formData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (updateError) {
      console.error('Update error details:', updateError);
      throw updateError;
    }

    // 7. Créer un snapshot du nouveau score
    const { error: snapshotError } = await supabase
      .from('score_snapshots')
      .insert({
        diagnostic_id: id,
        score: newScore,
        score_protection: categoryScores.protection,
        score_surveillance: categoryScores.surveillance,
        score_dissuasion: categoryScores.deterrence,
        snapshot_reason: 'action_completed',
        notes: `Action réalisée: ${selectedAction.action_title}`,
        created_at: new Date().toISOString()
      });

    if (snapshotError) {
      console.error('Snapshot error (non-blocking):', snapshotError);
    }

    toast.success('Action marquée comme réalisée !');
    setModalOpen(false);
    setSelectedAction(null);
    loadData();
  } catch (error) {
    console.error('Error completing action:', error);
    toast.error('Erreur lors de la validation');
  }
};

  const getPriorityConfig = (priority: Priority) => {
    return dpsTheme.colors.priority[priority];
  };

  const getStatusConfig = (status: Status | null) => {
    if (status === 'completed') return dpsTheme.colors.status.completed;
    if (status === 'planned' || status === 'in_progress') return dpsTheme.colors.status.planned;
    return dpsTheme.colors.status.notPlanned;
  };

  if (loading || isRepairing) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>
            {isRepairing ? 'Génération du plan d\'action...' : 'Chargement...'}
          </div>
        </div>
      </DPSLayout>
    );
  }

  if (!diagnostic) {
    return (
      <DPSLayout reportId={id || ''}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: dpsTheme.colors.text.muted }}>Diagnostic non trouvé</div>
        </div>
      </DPSLayout>
    );
  }

  const filters: Array<{ id: 'all' | Priority; label: string }> = [
    { id: 'all', label: 'Toutes' },
    { id: 'high', label: 'Haute priorité' },
    { id: 'medium', label: 'Moyenne' },
    { id: 'low', label: 'Basse' },
  ];

  const projection = calculateProjectedScore();

  return (
    <DPSLayout reportId={id || ''} onDownloadPDF={handleDownloadPDF}>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              Plan d'amélioration
            </h1>
            <p className="text-base sm:text-lg text-gray-400">
              Gérez vos actions et suivez leur réalisation
            </p>
          </div>

          <div
            className="flex gap-2 backdrop-blur-xl border border-gray-700/50 p-1 rounded-lg overflow-x-auto"
            style={{ background: dpsTheme.colors.background.card }}
          >
            {filters.map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  color: activeFilter === filter.id ? dpsTheme.colors.accent.blue.secondary : dpsTheme.colors.text.muted,
                  background: activeFilter === filter.id ? dpsTheme.colors.accent.blue.bg : 'transparent',
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <ActionProjectionBanner
          currentScore={diagnostic.security_score || diagnostic.score || 0}
          projectedScore={projection.projectedScore}
          selectedActionsCount={projection.selectedCount}
          estimatedCost={projection.estimatedCost}
        />

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.md }}>
            {filteredActions.length === 0 ? (
              <div
                style={{
                  background: dpsTheme.colors.background.card,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  borderRadius: dpsTheme.borderRadius.lg,
                  padding: dpsTheme.spacing['2xl'],
                  textAlign: 'center',
                }}
              >
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.text.muted} strokeWidth="1" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                </svg>
                <div style={{ fontSize: dpsTheme.typography.fontSize.lg, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.secondary, marginBottom: '8px' }}>
                  Aucune action {activeFilter !== 'all' ? 'avec cette priorité' : 'disponible'}
                </div>
                <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                  {activeFilter !== 'all' ? 'Essayez de changer le filtre de priorité' : 'Les actions recommandées apparaîtront ici après votre diagnostic'}
                </div>
              </div>
            ) : (
              filteredActions.map((action) => {
                const priorityConfig = getPriorityConfig(action.priority as Priority);
                const statusConfig = getStatusConfig(action.status);
                const isSimulated = simulatedActions.has(action.id);
                const hasStatus = action.status !== 'not_planned';

                return (
                  <div
                    key={action.id}
                    style={{
                      background: isSimulated ? 'rgba(16, 185, 129, 0.05)' : dpsTheme.colors.background.card,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${dpsTheme.colors.border.primary}`,
                      borderRadius: dpsTheme.borderRadius.lg,
                      padding: dpsTheme.spacing.lg,
                      opacity: action.status === 'completed' ? 0.7 : 1,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: dpsTheme.spacing.sm }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.base }}>
                            {action.action_title}
                          </span>
                          {action.selected_tier && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              fontSize: '10px',
                              fontWeight: dpsTheme.typography.fontWeight.medium,
                              borderRadius: '10px',
                              background: action.selected_tier === 'premium'
                                ? 'rgba(168, 85, 247, 0.1)'
                                : action.selected_tier === 'budget'
                                ? 'rgba(107, 114, 128, 0.1)'
                                : 'rgba(59, 130, 246, 0.1)',
                              border: action.selected_tier === 'premium'
                                ? '1px solid rgba(168, 85, 247, 0.3)'
                                : action.selected_tier === 'budget'
                                ? '1px solid rgba(107, 114, 128, 0.3)'
                                : '1px solid rgba(59, 130, 246, 0.3)',
                              color: action.selected_tier === 'premium'
                                ? '#a855f7'
                                : action.selected_tier === 'budget'
                                ? '#6b7280'
                                : '#3b82f6'
                            }}>
                              {action.selected_tier === 'premium' ? (
                                <>
                                  <Star style={{ width: '10px', height: '10px' }} />
                                  Premium
                                </>
                              ) : action.selected_tier === 'budget' ? (
                                <>
                                  <DollarSign style={{ width: '10px', height: '10px' }} />
                                  Budget
                                </>
                              ) : (
                                <>
                                  <Check style={{ width: '10px', height: '10px' }} />
                                  Recommandé
                                </>
                              )}
                            </span>
                          )}
                        </div>
                        {action.description && (
                          <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: dpsTheme.spacing.sm }}>
                            {action.description}
                          </div>
                        )}
                      </div>
                      {!hasStatus && (
                        <input
                          type="checkbox"
                          checked={isSimulated}
                          onChange={() => toggleSimulation(action.id)}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            accentColor: dpsTheme.colors.accent.green.primary,
                            marginLeft: '8px',
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: dpsTheme.spacing.sm, marginBottom: dpsTheme.spacing.md }}>
                      <div>
                        <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '4px' }}>
                          Priorité
                        </div>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: dpsTheme.typography.fontWeight.bold,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            background: priorityConfig.bg,
                            border: `1px solid ${priorityConfig.border}`,
                            color: priorityConfig.text,
                          }}
                        >
                          {getPriorityLabel(action.priority)}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '4px' }}>
                          Gain
                        </div>
                        <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.accent.green.primary, fontSize: dpsTheme.typography.fontSize.sm }}>
                          +{Number(action.score_impact)} pts
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '4px' }}>
                          Coût
                        </div>
                        {Number(action.estimated_cost) === 0 ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: dpsTheme.typography.fontWeight.medium,
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#10b981'
                          }}>
                            <FileCheck style={{ width: '12px', height: '12px' }} />
                            Gratuit
                          </span>
                        ) : (
                          <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary, fontSize: dpsTheme.typography.fontSize.sm }}>
                            {Number(action.selected_price || action.estimated_cost).toLocaleString('fr-FR')} €
                          </span>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '4px' }}>
                          Statut
                        </div>
                        {hasStatus ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              background: statusConfig.bg,
                              border: `1px solid ${statusConfig.border}`,
                              color: statusConfig.text,
                            }}
                          >
                            {getStatusLabel(action.status)}
                          </span>
                        ) : (
                          <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.sm }}>
                            Suggéré
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {ACTION_TO_EQUIPMENT_MAP[action.action_id] && Number(action.estimated_cost) > 0 && (
                        <button
                          onClick={() => {
                            const equipmentId = ACTION_TO_EQUIPMENT_MAP[action.action_id];
                            setSelectedEquipmentId(equipmentId);
                          }}
                          style={{
                            flex: 1,
                            minWidth: '120px',
                            padding: '10px 16px',
                            fontSize: '13px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#3b82f6',
                            fontWeight: dpsTheme.typography.fontWeight.medium,
                            minHeight: '44px',
                          }}
                        >
                          Voir détails
                        </button>
                      )}

                      {action.status === 'in_progress' && (
                        <>
                          <button
                            onClick={() => handleCompleteAction(action)}
                            style={{
                              flex: 1,
                              minWidth: '120px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              borderRadius: '8px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              cursor: 'pointer',
                              background: dpsTheme.colors.accent.green.bg,
                              border: `1px solid ${dpsTheme.colors.accent.green.border}`,
                              color: dpsTheme.colors.accent.green.primary,
                              minHeight: '44px',
                            }}
                          >
                            Réalisée
                          </button>
                          <button
                            onClick={() => handleCancelAction(action.id)}
                            style={{
                              flex: 1,
                              minWidth: '120px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              borderRadius: '8px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              cursor: 'pointer',
                              background: dpsTheme.colors.priority.high.bg,
                              border: `1px solid ${dpsTheme.colors.priority.high.border}`,
                              color: dpsTheme.colors.priority.high.text,
                              minHeight: '44px',
                            }}
                          >
                            Annuler
                          </button>
                        </>
                      )}

                      {action.status === 'planned' && (
                        <>
                          <button
                            onClick={() => handleStartAction(action.id)}
                            style={{
                              flex: 1,
                              minWidth: '120px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              borderRadius: '8px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              cursor: 'pointer',
                              background: dpsTheme.colors.accent.blue.bg,
                              border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
                              color: dpsTheme.colors.accent.blue.primary,
                              minHeight: '44px',
                            }}
                          >
                            Démarrer
                          </button>
                          <button
                            onClick={() => handleCancelAction(action.id)}
                            style={{
                              flex: 1,
                              minWidth: '120px',
                              padding: '10px 16px',
                              fontSize: '13px',
                              borderRadius: '8px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              cursor: 'pointer',
                              background: dpsTheme.colors.priority.high.bg,
                              border: `1px solid ${dpsTheme.colors.priority.high.border}`,
                              color: dpsTheme.colors.priority.high.text,
                              minHeight: '44px',
                            }}
                          >
                            Annuler
                          </button>
                        </>
                      )}

                      {!hasStatus && (
                        <button
                          onClick={() => handlePlanAction(action.id)}
                          style={{
                            flex: 1,
                            minWidth: '120px',
                            padding: '10px 16px',
                            fontSize: '13px',
                            borderRadius: '8px',
                            fontWeight: dpsTheme.typography.fontWeight.semibold,
                            cursor: 'pointer',
                            background: dpsTheme.colors.accent.blue.bg,
                            border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
                            color: dpsTheme.colors.accent.blue.primary,
                            minHeight: '44px',
                          }}
                        >
                          Planifier
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <div
            className="backdrop-blur-xl border border-gray-700/50 rounded-lg overflow-hidden"
            style={{ background: dpsTheme.colors.background.card }}
          >
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[800px]">
              <thead style={{ background: dpsTheme.colors.background.darker, borderBottom: `1px solid ${dpsTheme.colors.border.primary}` }}>
                <tr>
                  <th style={{ padding: '16px 20px', textAlign: 'center', width: '50px', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    ☑
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Action
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Détails
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Priorité
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Gain
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Coût
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Statut
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: dpsTheme.typography.fontSize.sm, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '48px 20px', textAlign: 'center' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={dpsTheme.colors.text.muted} strokeWidth="1" style={{ margin: '0 auto 16px', opacity: 0.5 }}>
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                      </svg>
                      <div style={{ fontSize: dpsTheme.typography.fontSize.lg, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.secondary, marginBottom: '8px' }}>
                        Aucune action {activeFilter !== 'all' ? 'avec cette priorité' : 'disponible'}
                      </div>
                      <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                        {activeFilter !== 'all' ? 'Essayez de changer le filtre de priorité' : 'Les actions recommandées apparaîtront ici après votre diagnostic'}
                      </div>
                    </td>
                  </tr>
                ) : filteredActions.map((action) => {
                  const priorityConfig = getPriorityConfig(action.priority as Priority);
                  const statusConfig = getStatusConfig(action.status);
                  const isSimulated = simulatedActions.has(action.id);
                  const hasStatus = action.status !== 'not_planned';

                  return (
                    <tr
                      key={action.id}
                      style={{
                        borderBottom: `1px solid ${dpsTheme.colors.border.secondary}`,
                        transition: 'all 0.2s',
                        opacity: action.status === 'completed' ? 0.6 : 1,
                        background: isSimulated ? 'rgba(16, 185, 129, 0.05)' : 'transparent',
                      }}
                    >
                      <td style={{ padding: dpsTheme.spacing.xl, textAlign: 'center' }}>
                        {!hasStatus && (
                          <input
                            type="checkbox"
                            checked={isSimulated}
                            onChange={() => toggleSimulation(action.id)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: dpsTheme.colors.accent.green.primary,
                            }}
                          />
                        )}
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontWeight: dpsTheme.typography.fontWeight.semibold, 
                            color: dpsTheme.colors.text.primary 
                          }}>
                            {action.action_title}
                            {action.selected_tier && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: dpsTheme.typography.fontWeight.medium,
                                borderRadius: '12px',
                                background: action.selected_tier === 'premium' 
                                  ? 'rgba(168, 85, 247, 0.1)'
                                  : action.selected_tier === 'budget'
                                  ? 'rgba(107, 114, 128, 0.1)'
                                  : 'rgba(59, 130, 246, 0.1)',
                                border: action.selected_tier === 'premium' 
                                  ? '1px solid rgba(168, 85, 247, 0.3)'
                                  : action.selected_tier === 'budget'
                                  ? '1px solid rgba(107, 114, 128, 0.3)'
                                  : '1px solid rgba(59, 130, 246, 0.3)',
                                color: action.selected_tier === 'premium' 
                                  ? '#a855f7'
                                  : action.selected_tier === 'budget'
                                  ? '#6b7280'
                                  : '#3b82f6'
                              }}>
                                {action.selected_tier === 'premium' ? (
                                  <>
                                    <Star style={{ width: '12px', height: '12px' }} />
                                    Premium
                                  </>
                                ) : action.selected_tier === 'budget' ? (
                                  <>
                                    <DollarSign style={{ width: '12px', height: '12px' }} />
                                    Budget
                                  </>
                                ) : (
                                  <>
                                    <Check style={{ width: '12px', height: '12px' }} />
                                    Recommandé
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted }}>
                            {action.description || ''}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        {ACTION_TO_EQUIPMENT_MAP[action.action_id] && Number(action.estimated_cost) > 0 ? (
                          <button
                            onClick={() => {
                              const equipmentId = ACTION_TO_EQUIPMENT_MAP[action.action_id];
                              setSelectedEquipmentId(equipmentId);
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              color: '#3b82f6',
                            }}
                          >
                            Détails
                          </button>
                        ) : (
                          <span style={{ color: dpsTheme.colors.text.muted, fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: dpsTheme.typography.fontSize.xs,
                            fontWeight: dpsTheme.typography.fontWeight.bold,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            background: priorityConfig.bg,
                            border: `1px solid ${priorityConfig.border}`,
                            color: priorityConfig.text,
                          }}
                        >
                          {getPriorityLabel(action.priority)}
                        </span>
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.accent.green.primary }}>
                          +{Number(action.score_impact)} pts
                        </span>
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        {Number(action.estimated_cost) === 0 ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: dpsTheme.typography.fontWeight.medium,
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            color: '#10b981'
                          }}>
                            <FileCheck style={{ width: '14px', height: '14px' }} />
                            Gratuit
                          </span>
                        ) : (
                          <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
                            {Number(action.selected_price || action.estimated_cost).toLocaleString('fr-FR')} €
                          </span>
                        )}
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        {hasStatus ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontSize: dpsTheme.typography.fontSize.sm,
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              background: statusConfig.bg,
                              border: `1px solid ${statusConfig.border}`,
                              color: statusConfig.text,
                            }}
                          >
                            {action.status === 'completed' && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                            {action.status === 'planned' && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            )}
                            {action.status === 'in_progress' && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                            )}
                            {getStatusLabel(action.status)}
                          </span>
                        ) : (
                          <span style={{ color: dpsTheme.colors.text.muted, fontSize: dpsTheme.typography.fontSize.sm }}>
                            Suggéré
                          </span>
                        )}
                      </td>
                      <td style={{ padding: dpsTheme.spacing.xl }}>
                        {action.status === 'completed' ? (
                          <span style={{ color: dpsTheme.colors.text.muted }}>—</span>
                        ) : action.status === 'in_progress' ? (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleCompleteAction(action)}
                              style={{
                                padding: '6px 12px',
                                fontSize: dpsTheme.typography.fontSize.sm,
                                borderRadius: '6px',
                                fontWeight: dpsTheme.typography.fontWeight.semibold,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: dpsTheme.colors.accent.green.bg,
                                border: `1px solid ${dpsTheme.colors.accent.green.border}`,
                                color: dpsTheme.colors.accent.green.primary,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.25)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = dpsTheme.colors.accent.green.bg;
                              }}
                            >
                              Réalisée
                            </button>
                            <button
                              onClick={() => handleCancelAction(action.id)}
                              style={{
                                padding: '6px 12px',
                                fontSize: dpsTheme.typography.fontSize.sm,
                                borderRadius: '6px',
                                fontWeight: dpsTheme.typography.fontWeight.semibold,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: dpsTheme.colors.priority.high.bg,
                                border: `1px solid ${dpsTheme.colors.priority.high.border}`,
                                color: dpsTheme.colors.priority.high.text,
                              }}
                            >
                              Annuler
                            </button>
                          </div>
                        ) : action.status === 'planned' ? (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleStartAction(action.id)}
                              style={{
                                padding: '6px 12px',
                                fontSize: dpsTheme.typography.fontSize.sm,
                                borderRadius: '6px',
                                fontWeight: dpsTheme.typography.fontWeight.semibold,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: 'rgba(251, 191, 36, 0.15)',
                                border: '1px solid rgba(251, 191, 36, 0.3)',
                                color: 'rgb(251, 191, 36)',
                              }}
                            >
                              En cours
                            </button>
                            <button
                              onClick={() => handleCompleteAction(action)}
                              style={{
                                padding: '6px 12px',
                                fontSize: dpsTheme.typography.fontSize.sm,
                                borderRadius: '6px',
                                fontWeight: dpsTheme.typography.fontWeight.semibold,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: dpsTheme.colors.accent.green.bg,
                                border: `1px solid ${dpsTheme.colors.accent.green.border}`,
                                color: dpsTheme.colors.accent.green.primary,
                              }}
                            >
                              Réalisée
                            </button>
                            <button
                              onClick={() => handleCancelAction(action.id)}
                              style={{
                                padding: '6px 12px',
                                fontSize: dpsTheme.typography.fontSize.sm,
                                borderRadius: '6px',
                                fontWeight: dpsTheme.typography.fontWeight.semibold,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: dpsTheme.colors.priority.high.bg,
                                border: `1px solid ${dpsTheme.colors.priority.high.border}`,
                                color: dpsTheme.colors.priority.high.text,
                              }}
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handlePlanAction(action.id)}
                            style={{
                              padding: '6px 12px',
                              fontSize: dpsTheme.typography.fontSize.sm,
                              borderRadius: '6px',
                              fontWeight: dpsTheme.typography.fontWeight.semibold,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              background: dpsTheme.colors.accent.blue.bg,
                              border: `1px solid ${dpsTheme.colors.accent.blue.border}`,
                              color: dpsTheme.colors.accent.blue.secondary,
                            }}
                          >
                            Planifier
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </main>

      <ActionCompletionModal
        isOpen={modalOpen}
        actionTitle={selectedAction?.action_title || ''}
        onClose={() => {
          setModalOpen(false);
          setSelectedAction(null);
        }}
        onConfirm={handleConfirmCompletion}
      />
      {selectedEquipmentId && (
  <EquipmentDetailsModal
    equipmentId={selectedEquipmentId}
    isOpen={selectedEquipmentId !== null}
    onClose={() => setSelectedEquipmentId(null)}
    onSelectOption={(tier, price) => {
      // Trouver l'action correspondant à l'équipement sélectionné
      // On cherche dans ACTION_TO_EQUIPMENT_MAP pour retrouver l'action_id
      const actionId = Object.keys(ACTION_TO_EQUIPMENT_MAP).find(
        key => ACTION_TO_EQUIPMENT_MAP[key] === selectedEquipmentId
      );
      
      if (!actionId) {
        console.error('Action ID not found for equipment:', selectedEquipmentId);
        toast.error('Action non trouvée');
        return;
      }

      // Trouver l'action dans la liste
      const actionToUpdate = actions.find(a => a.action_id === actionId);
      
      if (actionToUpdate) {
        supabase
          .from('improvement_actions_history')
          .update({
            selected_tier: tier,
            selected_price: price,
            estimated_cost: price,
            updated_at: new Date().toISOString()
          })
          .eq('id', actionToUpdate.id)
          .then(() => {
            toast.success(`Option ${tier === 'premium' ? 'Premium' : tier === 'budget' ? 'Budget' : 'Recommandée'} sélectionnée`);
            setSelectedEquipmentId(null); // Fermer le modal
            loadData(); // Recharger les données
          })
          .catch((error) => {
            console.error('Error updating action:', error);
            toast.error('Erreur lors de la mise à jour');
          });
      } else {
        console.error('Action not found in actions list:', actionId);
        toast.error('Action non trouvée dans la liste');
      }
    }}
  />
)}
    </DPSLayout>
  );
};